import { useAuthStore } from "@/stores/auth";
import { createLogger } from "@/services/logger/logger.service";
import { useFingerprint } from "@/composables/useFingerprint";
import { axiomService } from "@/services/axiom/axiom.service";

const logger = createLogger("UseApiClient");
import { useRouter } from "vue-router";
import { authService } from "@/services/auth/auth.service";

export interface ApiResponse<T = unknown> {
  data?: T;
  response: Response;
}

/**
 * Extract server timings from Response object
 * Attempts to use Performance API first, falls back to parsing Server-Timing header
 */
function extractServerTimings(
  response: Response,
  url: string,
): Record<string, number> | undefined {
  try {
    // Try to get timings from Performance API (most reliable)
    const entries = performance.getEntriesByType(
      "resource",
    ) as PerformanceResourceTiming[];
    const entry = entries.find((e) => e.name === url || e.name.includes(url));

    if (entry && "serverTiming" in entry && entry.serverTiming) {
      const timings: Record<string, number> = {};
      entry.serverTiming.forEach((timing: PerformanceServerTiming) => {
        timings[timing.name] = timing.duration || 0;
      });
      return Object.keys(timings).length > 0 ? timings : undefined;
    }

    // Fallback: Parse Server-Timing header directly
    const serverTimingHeader = response.headers.get("Server-Timing");
    if (serverTimingHeader) {
      const timings: Record<string, number> = {};
      // Parse format: "metric;dur=123.4;desc=description, metric2;dur=456.7"
      const metrics = serverTimingHeader.split(",");

      metrics.forEach((metric) => {
        const parts = metric.trim().split(";");
        const name = parts[0].trim();
        const durPart = parts.find((p) => p.trim().startsWith("dur="));

        if (durPart) {
          const duration = parseFloat(durPart.split("=")[1]);
          if (!isNaN(duration)) {
            timings[name] = duration;
          }
        }
      });

      return Object.keys(timings).length > 0 ? timings : undefined;
    }
  } catch (error) {
    // Silently fail - server timings are optional
    logger.debug("Failed to extract server timings:", error);
  }

  return undefined;
}

export function useApiClient() {
  const router = useRouter();

  /**
   * Enhanced fetch wrapper that handles 401 responses globally and tracks API calls
   */
  async function apiRequest<T = unknown>(
    url: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    const method = options.method || "GET";
    const startTime = performance.now();
    let fetchStartTime = 0;
    let fetchEndTime = 0;

    try {
      // Track when actual fetch starts
      fetchStartTime = performance.now();
      const response = await fetch(url, options);
      fetchEndTime = performance.now();

      const totalDuration = performance.now() - startTime;
      const networkDuration = fetchEndTime - fetchStartTime;
      const overheadDuration = totalDuration - networkDuration;

      // Parse response data first to check for API-level errors
      let data: T | undefined;
      let apiError: string | undefined;
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        try {
          data = await response.json();
          // Check for API-level error in response body
          if (data && typeof data === "object") {
            const apiData = data as Record<string, unknown>;
            if (apiData.success === false || apiData.error) {
              apiError = apiData.message || apiData.error || "API error";
            }
          }
        } catch (e) {
          logger.warn("Failed to parse JSON response:", e);
        }
      }

      // Extract server timings if available
      const serverTimings = extractServerTimings(response, url);


      // Track ALL API calls - successful and failed
      axiomService.trackApiCall(url, method, response.status, totalDuration, {
        success: response.ok && !apiError,
        networkDuration,
        overheadDuration,
        totalDuration,
        statusText: response.statusText,
        apiError,
        contentType,
        responseSize: response.headers.get("content-length") || undefined,
        timings: serverTimings,
      });

      // Handle 401 Unauthorized responses globally
      if (response.status === 401) {
        logger.warn("🚨 401 Unauthorized - redirecting to login");

        // Clear authentication state
        const authStore = useAuthStore();
        await authStore.logout();

        // Redirect to login page
        router.push({ name: "Login" });

        // Throw error to prevent further processing
        throw new Error("Unauthorized - redirected to login");
      }

      // Log non-200 responses as warnings
      if (!response.ok || apiError) {
        logger.warn(`API call failed: ${method} ${url}`, {
          status: response.status,
          statusText: response.statusText,
          apiError,
          duration: totalDuration,
        });
      }

      return { data, response };
    } catch (error) {
      const totalDuration = performance.now() - startTime;
      const networkDuration =
        fetchEndTime > 0 ? fetchEndTime - fetchStartTime : 0;

      // Track failed API call with detailed error info
      axiomService.trackApiCall(url, method, 0, totalDuration, {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
        errorType: error instanceof Error ? error.name : "UnknownError",
        networkDuration,
        totalDuration,
        failed: true,
      });

      logger.error(`API call exception: ${method} ${url}`, error);
      throw error;
    }
  }

  /**
   * Authenticated API request with automatic Bearer token and device fingerprint
   */
  async function authenticatedRequest<T = unknown>(
    url: string,
    options: RequestInit = {},
  ): Promise<ApiResponse<T>> {
    // Get token from auth service
    const token = await authService.getAccessToken();

    if (!token) {
      throw new Error("No authentication token available");
    }

    // Get device fingerprint
    const fingerprint = useFingerprint();
    const deviceId =
      fingerprint.visitorId.value || (await fingerprint.initFingerprint());

    const authenticatedOptions: RequestInit = {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Device-Id": deviceId,
        "X-Device-Fingerprint": deviceId, // Alias for backwards compatibility
        ...options.headers,
      },
    };

    return apiRequest<T>(url, authenticatedOptions);
  }

  return {
    apiRequest,
    authenticatedRequest,
  };
}
